// IMPORTANT: After modifying this file, update CHANGELOG.md with a summary of your changes.
// Add clear comments for non-obvious changes, including what replaced prior behavior, to avoid repeating failed fixes.

"use client";

import { useState } from "react";
import Link from "next/link";
import type { BlockConfigOf } from "@/lib/blocks/schemas";

type Config = BlockConfigOf<"testimonial">;
type Item = Config["items"][number];

/**
 * The attribution line as a link (#111). Placement is VariantA from the
 * link prototype on branch `prototype/testimonial-link` (commit 07b573b):
 * the existing text becomes the link, so an entry without `href` renders
 * byte-identical to before. `→` marks an internal page, `↗` an external one
 * that opens in a new tab — the linkColumn convention for `external`.
 */
function AttributionLink({
  href,
  external,
  dark,
  className,
  children,
}: {
  href: string;
  external: boolean;
  dark: boolean;
  className: string;
  children: React.ReactNode;
}) {
  const cls = `${className} block w-fit underline underline-offset-4 ${
    dark
      ? "decoration-stone-600 hover:text-white hover:decoration-white"
      : "decoration-stone-400 hover:text-black hover:decoration-black"
  }`;
  const arrow = <span aria-hidden="true">{external ? " ↗" : " →"}</span>;
  return external ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
      {children}
      {arrow}
    </a>
  ) : (
    <Link href={href} className={cls}>
      {children}
      {arrow}
    </Link>
  );
}

/**
 * Client renderer for the testimonial card body. Split out of
 * TestimonialBlock.tsx (following the ServicesTabsBlock/ServicesTabsClient
 * split) because a homepage-parity test calls TestimonialBlock as a plain
 * function and feeds the result to renderToStaticMarkup — a hook there
 * would throw an invalid-hook-call. This client component owns the active
 * index and all interaction; the parent stays hook-free.
 *
 * Fixed-height card lifted from the validated layout prototype, VariantB
 * in src/app/(public)/prototype-testimonials/variants.tsx as of commit
 * 5d9020b. That route has since been deleted; read it from git history.
 */
export function TestimonialCarousel({
  items,
  muted,
  dark,
}: {
  items: Item[];
  muted: string;
  // Which card background this sits on. The controls are drawn in the
  // card's contrasting ink, so they need to know: on the white card an
  // active `bg-white` dot is invisible against the card, and a
  // `hover:bg-stone-800` button turns near-black under black text.
  dark: boolean;
}) {
  const [active, setActive] = useState(0);
  const idx = Math.min(active, items.length - 1);
  const entry = items[idx];
  const showControls = items.length > 1;

  return (
    // Fills the remaining flex-column space inside the parent card (whose
    // height is the deliberately FIXED h-[36rem]/md:h-[34rem] — see
    // TestimonialBlock.tsx). min-h-0 lets this flex child shrink below its
    // content size so the blockquote's own overflow-y-auto actually kicks
    // in instead of the card growing to fit a long quote.
    <div className="flex-1 flex flex-col min-h-0">
      <blockquote
        tabIndex={0}
        role="region"
        aria-label="Testimonial quote, scrollable"
        className="font-[family-name:var(--font-display)] text-2xl md:text-3xl leading-snug mb-8 flex-1 overflow-y-auto no-scrollbar focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-current"
      >
        “{entry.quote}”
      </blockquote>
      <div className="shrink-0">
        <div className="flex items-center gap-4">
          {entry.avatarUrl && (
            <img
              src={entry.avatarUrl}
              alt={`${entry.name} avatar`}
              loading="lazy"
              decoding="async"
              className="w-14 h-14 rounded-full object-cover brutal-border-sm"
            />
          )}
          <div>
            {entry.href && !entry.role ? (
              // No role to carry the link, so the name carries it instead —
              // otherwise a linked entry with a blank role has no link at all.
              <AttributionLink
                href={entry.href}
                external={entry.external}
                dark={dark}
                className="font-bold"
              >
                {entry.name}
              </AttributionLink>
            ) : (
              <div className="font-bold">{entry.name}</div>
            )}
            {entry.role &&
              (entry.href ? (
                <AttributionLink
                  href={entry.href}
                  external={entry.external}
                  dark={dark}
                  className={`text-sm uppercase tracking-widest ${muted}`}
                >
                  {entry.role}
                </AttributionLink>
              ) : (
                <div className={`text-sm uppercase tracking-widest ${muted}`}>
                  {entry.role}
                </div>
              ))}
          </div>
        </div>
        {/* Exactly one entry: no prev/next, no dots — not hidden with
            CSS, not rendered — so the quote region takes the freed
            space and the card height is unaffected. */}
        {showControls && (
          <div className="flex items-center gap-3 mt-8">
            <button
              type="button"
              onClick={() =>
                setActive((n) => (n - 1 + items.length) % items.length)
              }
              className={`brutal-border-sm px-3 py-1 text-sm ${
                dark ? "hover:bg-stone-800" : "hover:bg-stone-100"
              }`}
              aria-label="Previous testimonial"
            >
              ←
            </button>
            <button
              type="button"
              onClick={() => setActive((n) => (n + 1) % items.length)}
              className={`brutal-border-sm px-3 py-1 text-sm ${
                dark ? "hover:bg-stone-800" : "hover:bg-stone-100"
              }`}
              aria-label="Next testimonial"
            >
              →
            </button>
            <div className="flex gap-1 ml-2">
              {items.map((_, n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setActive(n)}
                  aria-label={`Testimonial ${n + 1}`}
                  aria-current={n === idx ? "true" : undefined}
                  // Hit area is enlarged to ~24px for touch/pointer
                  // accessibility without growing the visible dot, which
                  // stays the original 10px inner span.
                  className="w-6 h-6 flex items-center justify-center"
                >
                  <span
                    aria-hidden="true"
                    className={`w-2.5 h-2.5 rounded-full ${
                      n === idx
                        ? dark
                          ? "bg-white"
                          : "bg-stone-900"
                        : dark
                          ? "bg-stone-600"
                          : "bg-stone-400"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
